<template>
    <div style="min-height: calc(100vh - 158px);" class="card">
        <div v-if="loading" style="position: fixed; top: 0; bottom: 0; left:0; right: 0; background: black z-index: 9999; opacity: 0">dd</div>
        <div style="background: #DBE7F2" class="p-2">
            <TitleButton
                :title="pageTitle"
                :showExtraBtn="isCreatePage"
                extraBtnTitle="Browse"
                @onClickExtraBtn="onClickBrowse"
                @onClickCloseButton="goToList"
            >
                <template #title-details>
                    <a
                        download
                        href="/file/journal-import-file.xlsx"
                        style="margin-right: 25px;"
                        class="btn btn-outline-secondary waves-effect waves-float waves-light"
                    >Download Sample
                    </a>
                </template>
            </TitleButton>
        </div>

        <div class="p-2">
            <div v-if="isUploading" class="row mt-4">
                <div class="col-md-6">
                        <label for="">{{fileName}}</label>
                    <div class="progress progress-bar-primary">
                        <div 
                            class="progress-bar" 
                            role="progressbar"
                            aria-valuemax="100" 
                            :style="{width: progress+'%'}" :aria-describedby="fileName"></div>
                    </div>
                </div>
            </div>
            <input type="file" class="d-none" ref="uploader" @change="handleFileUpload">
            
            <template v-if="sheet.id">
                <Ledger
                    :ledgers="sheet.ledgers"
                    :businesses="businesses"
                    :costCentres="costCentres"
                    :projects="projects"
                    :ngoabHeads="ngoabHeads"
                    :programs="programs"
                    :projectActivity="projectActivity"
                    @onclickUpdate="onclickUpdate"
                    @onclickDelete="onclickDelete"
                />
            </template>

           <button
                :disabled="loading"
                v-if="sheet.id" 
                @click="onClickStore" 
                class="btn btn-primary mt-1"
            >
                Save
            </button>
        </div>
        <Loader v-if="loading"/>
    </div>
</template>

<script>
import handleJournal from '@/services/modules/journal'
import { inject } from 'vue'
import {mapMutations} from 'vuex'

import TitleButton from '@/components/atom/TitleButton'
import Ledger from '@/components/molecule/journal-import/Ledger'
import Loader from '@/components/atom/LoaderComponent'
import handleDropdowns from '@/services/modules/dropdowns'

export default {
    name: 'JournalCreateEdit',
    components: {
        TitleButton,
        Ledger,
        Loader
    },
    data: () => ({
        sheet: {},
        selectedFile: null,
        fileName: '',
        isUploading: false,
        businesses: [],
        costCentres: [],
        projects: [],
        programs: [],
        projectActivity: [],
        ngoabHeads: [],
    }),
    computed: {
        companyId () {
            return this.$route.params.companyId
        },
        uploadLogId () {
            return this.$route.params.uploadLogId
        },
        start () {
            return this.$route.query.start
        },
        end () {
            return this.$route.query.end
        },
        isCreatePage() {
            return this.$route.name === 'journal-import'
        },
        pageTitle() {
            if(this.isCreatePage) return 'Imported Transaction List'
            return 'Edit Transaction'
        },
        progress () {
            return this.$store.state.progress
        }
    },
    methods: {
        ...mapMutations({
            updateProgress: 'updateProgress'
        }),

        onClickStore() {
            let storeValidity = this.checkStoreValidity()
            if(!storeValidity) {
                this.showError('Manually fix all entry first');
                return
            }

            let unbalanced = this.findUnbalancedVoucher()
            if(unbalanced) {
                this.showError(`Voucher ${unbalanced} total debit and total credit must be matched`);
                return
            }

            this.storeAllLedgers();
        },

        async storeAllLedgers() {
            let data = {
                company_id: this.companyId
            }

            try {
                this.loading = true
                let res = await this.storeAllVouchers(this.sheet.id, data)
                if(!res.status) {
                    this.showError(res.message)
                    return
                }
                this.showSuccess(res.message)
                this.goToList()
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.data.message)
                }
            } finally {
                this.loading = false
            }
        },

        checkStoreValidity() {
            let option = true;
            Object.keys(this.sheet.ledgers).forEach(key => {
                this.sheet.ledgers[key].ledgers.forEach(ledger => {
                    if(ledger.validation_account_head === false) option = false
                    if(ledger.validation_date === false) option = false
                    if(ledger.validation_debit_credit === false) option = false
                })
            })
            return option
        },

        findUnbalancedVoucher() {
            let unbalanced = null;
            Object.keys(this.sheet.ledgers).forEach(key => {
                if(unbalanced) return
                if(this.sheet.ledgers[key].status == 'completed') return

                let debit = 0;
                let credit = 0;
                this.sheet.ledgers[key].ledgers.forEach(ledger => {
                    if(ledger.debit) debit += parseFloat(ledger.debit)
                    if(ledger.credit) credit += parseFloat(ledger.credit)
                })

                if(debit.toFixed(2) !== credit.toFixed(2)) unbalanced = key
            })
            return unbalanced
        },

        async onclickUpdate (data) {
            let fd = new FormData();
            fd.append('company_id', this.companyId)
            fd.append('voucher_no', data.keyName)
            fd.append('ledgers', JSON.stringify(data.ledger.ledgers))
            try {
                this.loading = true
                let res = await this.updateIndividual(this.sheet.id, fd)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.getSheetData()
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.loading = false
            }
        },

        async onclickDelete (data) {
            let query = 'company_id=' + this.companyId + '&voucher_no='+data.keyName;
            try {
                this.loading = true
                let res = await this.removeIndividual(this.sheet.id, query)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.getSheetData()
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.loading = false
            }
        },

        async getSheetData() {
            let uploadId = this.uploadLogId ? this.uploadLogId : this.sheet.id

            try {
                this.loading = true
                let res = await this.fetchSheet(uploadId, `?company_id=${this.companyId}`)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.sheet = res.data
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.loading = false
            }
        },

        async uploadJournalOnServer(data) {
            try {
                this.isUploading = true
                let res = await this.uploadJournal(data)
                if(!res.status) {
                    this.showError(res.message)
                }
                if(res.status) {
                    this.sheet = res.data
                    this.selectedFile = null
                    this.fileName = ''
                }
            } catch (err) {
                if(!err.response) {
                    this.showError('Something is wrong. Check your connectivity!!')
                }
                if(err.response) {
                    this.showError(err.response.message)
                }
            } finally {
                this.isUploading = false
                this.updateProgress(0)
            }
        },

        goToList () {
            this.$router.push({name: 'journal-import-list', params: this.$route.params, query: this.$route.query})
        },

        onClickBrowse() {
            this.$refs.uploader.click()
        },

        handleFileUpload (event) {
            this.selectedFile = event.target.files[0];
            this.fileName = event.target.files[0].name
            let fd = new FormData();
            fd.append('company_id', this.companyId)
            fd.append('note', ' ')
            fd.append('mop_references', ' ')
            fd.append('sheet', this.selectedFile)
            this.uploadJournalOnServer(fd)
        },

        async getDropdowns () {
            let query = '?company_id=' + this.companyId
                + '&is_business=1&is_cost_centre=1&is_project=1'
                + '&is_program=1&is_fd6_accounts=1&is_project_activity=1'

            try {
                let res = await this.fetchDropdownList(query)
                if(!res.data) return

                this.businesses = res.data.business_list
                this.costCentres = res.data.cost_centre_list
                this.projects = res.data.project_list
                this.programs = res.data.program_list
                this.ngoabHeads = res.data.fd6_account_list
                this.projectActivity = res.data.project_activity_list
            } catch (err) {
                this.showError('Failed to load dropdown data')
            }
        }
    },

    setup() {
        const showError =  inject('showError');
        const showSuccess =  inject('showSuccess');

        const {
            updateIndividual,
            removeIndividual,
            storeAllVouchers,
            fetchSheet,
            uploadJournal,
            loading
        } = handleJournal()

      const { fetchDropdownList } = handleDropdowns()

        return {
            updateIndividual,
            removeIndividual,
            storeAllVouchers,
            fetchSheet,
            uploadJournal,
            fetchDropdownList,
            loading,
            showError,
            showSuccess
        }
    },

    mounted() {
      if(!this.isCreatePage) {
        this.getSheetData()
      }

      this.getDropdowns()
    }
}
</script>

<style scoped>
    .cancel-btn{
        background-color: #F0F2F5; color: #7D7D7D;
    }
    .cancel-btn:hover{
        background-color: #e9edf5;
    }
    .btn-close {
        width: 30px;
        height: 30px;
        background-color: #E4E6EB;
        border-radius: 50%;
    }
    .btn-close:hover {
        background-color: #D8DADF;
    }
</style>